import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { LotteryAccessService } from './lottery-access.service';

export interface Menu {
  headTitle?: string;
  headTitle2?: string;
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeValue?: string;
  badgeClass?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  Menusub?: boolean;
  target?: boolean;
  locked?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);

  private apiCredentialsVisible = true;

  public search = false;
  public language = false;
  public megaMenu = false;
  public levelMenu = false;
  public megaMenuColapse = window.innerWidth < 1199;
  public collapseSidebar = window.innerWidth < 991;
  public horizontal = window.innerWidth >= 991;
  public fullScreen = false;

  public MENUITEMS: Menu[] = [];
  items = new BehaviorSubject<Menu[]>([]);

  constructor(
    private router: Router,
    private authService: AuthService,
    private http: HttpClient,
    private lotteryAccessService: LotteryAccessService
  ) {
    this.setScreenWidth(window.innerWidth);

    fromEvent(window, 'resize')
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);

        if (evt.target.innerWidth < 991) {
          this.collapseSidebar = true;
          this.megaMenu = false;
          this.levelMenu = false;
        }

        if (evt.target.innerWidth < 1199) {
          this.megaMenuColapse = true;
        }
      });

    if (window.innerWidth < 991) {
      this.router.events.subscribe(() => {
        this.collapseSidebar = true;
        this.megaMenu = false;
        this.levelMenu = false;
      });
    }

    const isExternal = window.location.pathname.startsWith('/external/');

    if (!isExternal && !this.authService.isAdmin() && !this.authService.isCrypto()) {
      this.checkApiCredentialsVisibility();
    } else {
      this.loadMenuItems();
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(null);
    this.unsubscriber.complete();
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  private buildCommonMenu(): Menu[] {
    return [
      {
        headTitle: 'common.lotteryManagement',
      },
      {
        title: 'common.overview',
        icon: 'fe fe-bar-chart',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/overview',
      },
      {
        title: 'common.onboarding',
        icon: 'fe fe-upload-cloud',
        type: 'link',
        Menusub: false,
        active: false,
        locked: true,
        path: '/',
      },
      {
        title: 'common.transactionalAccounts',
        icon: 'fe fe-grid',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/transactional-accounts',
      },
      {
        title: 'common.operation',
        icon: 'fe fe-briefcase',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/operation-indicators', title: 'common.indicators', type: 'link' },
          { path: '/product', title: 'Modalidade Lotérica', type: 'link' },
          { path: '/licensing', title: 'Licenciamentos', type: 'link' },
          { path: '/operators', title: 'common.operators', type: 'link' },
        ],
      },
      {
        title: 'common.playersUsers',
        icon: 'fe fe-award',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/users-indicators', title: 'common.indicators', type: 'link' },
          { path: '/player-list', title: 'common.playerList', type: 'link' },
        ],
      },
      {
        title: 'common.monitoring',
        icon: 'fe fe-eye',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/monitoring',
      },
      {
        title: 'common.managementFinance',
        icon: 'fe fe-dollar-sign',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/finance-indicators', title: 'common.indicators', type: 'link' },
          { path: '/operator-list', title: 'common.operatorList', type: 'link' },
          { path: '/financial-summary', title: 'common.financialSummary', type: 'link' },
          { path: '/bet-summary', title: 'common.betSummary', type: 'link' },
          { path: '/raffle-summary', title: 'common.raffleSummary', type: 'link', locked: true },
        ],
      },
      {
        headTitle: 'common.administration',
      },
      {
        title: 'common.users',
        icon: 'fe fe-user-plus',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/user-config-lot',
      },
      {
        title: 'Log de Usuários',
        icon: 'fe fe-layers',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/activity-log',
      },
      {
        title: 'common.supportTickets',
        icon: 'fe fe-bookmark',
        type: 'link',
        Menusub: false,
        active: false,
        locked: true,
        path: '/indicators',
      },
      {
        headTitle: 'common.lotteryOperator',
      },
      {
        title: 'common.dashboard',
        icon: 'fe fe-bar-chart-2',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/reports',
      },
      {
        title: 'common.subAccounts',
        icon: 'fe fe-grid',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/sub-accounts',
      },
      {
        title: 'common.transactions',
        icon: 'fe fe-edit',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/extrato', title: 'common.movement', type: 'link' },
          { path: '/resumo', title: 'common.summary', type: 'link' },
          { path: '/infracoes', title: 'common.infractions', type: 'link' },
        ],
      },
      {
        title: 'common.commissions',
        icon: 'fe fe-percent',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/comissoes', title: 'common.billing', type: 'link' },
          { path: '/carteira-comissoes', title: 'common.wallet', type: 'link' },
        ],
      },
      {
        title: 'common.deposit',
        icon: 'fe fe-log-in',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/deposito', title: 'common.qrCode', type: 'link' },
          { path: '/depositoPix', title: 'common.pixKey', type: 'link' },
          { path: '/cashIn', title: 'Cash In', type: 'link' },
        ],
      },
      {
        title: 'common.withdraw',
        icon: 'fe fe-log-out',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/transferencia', title: 'common.pixKey', type: 'link' },
          { path: '/saque-copia-cola', title: 'common.copyPaste', type: 'link' },
          { path: '/saque-boleto', title: 'common.boletoWithdrawal', type: 'link' },
        ],
      },
      {
        title: 'common.transferBalance',
        icon: 'icon-wallet',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/transfer-balance',
      },
      {
        title: 'common.approveWithdrawals',
        icon: 'fe fe-unlock',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/approve-withdraw',
      },
      {
        title: 'common.ggrReserve',
        icon: 'fe fe-dollar-sign',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/reserva-financeira',
      },
      {
        title: 'common.cryptoassets',
        icon: 'fa fa-bitcoin',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/criptomoeda', title: 'common.withdraw', type: 'link' },
          { path: '/wallet', title: 'common.cryptoWallet', type: 'link' },
        ]
      },
      {
        headTitle: 'common.configurations',
      },
      {
        title: 'common.account',
        icon: 'fe fe-user',
        type: 'sub',
        Menusub: true,
        active: false,
        children: [
          { path: '/account/view', title: 'common.information', type: 'link' },
          { path: '/webhook', title: 'common.webHook', type: 'link' },
          { path: '/api-credentials', title: 'common.apiCredentials', type: 'link' },
        ],
      },
      {
        title: 'common.users',
        icon: 'fe fe-user-plus',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/user-config',
      },
      {
        title: 'common.bankingCredentials',
        icon: 'fe fe-shield',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/banking-credentials',
      },
      {
        title: 'Log de Integração',
        icon: 'fe fe-layers',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/technical-events',
      },
      {
        title: 'common.supportTickets',
        icon: 'fe fe-bookmark',
        type: 'link',
        Menusub: false,
        active: false,
        locked: true,
        path: '/indicators',
      },
      {
        title: 'common.checkout',
        icon: 'fe fe-settings',
        type: 'link',
        Menusub: false,
        active: false,
        path: '/checkout-config',
      },
    ];
  }

  public MENU_ADMIN: Menu[] = [
    {
      title: 'Contas',
      icon: 'fe fe-user',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/account/account',
    },
    {
      title: 'Aprovar Saques',
      icon: 'fe fe-unlock',
      type: 'link',
      Menusub: false,
      active: false,
      path: '/approve-withdraw-admin',
    },
  ];

  public MENU_CRYPTO: Menu[] = [
    {
      title: 'common.cryptoConversion',
      icon: 'fa fa-bitcoin',
      path: '/cripto',
      type: 'link',
      Menusub: false,
      active: false,
    },
    {
      title: 'common.account',
      icon: 'fe fe-user',
      path: '/account/view',
      type: 'link',
      Menusub: false,
      active: false,
    },
  ];

  private depositMenu(menu: Menu[]): void {
    const depositMenu = menu.find((m) => m.title === 'common.deposit');
    if (!depositMenu || !depositMenu.children) return;

    const origin = this.authService.getOriginCurrency();

    if (origin === 'BRL') {
      depositMenu.children = depositMenu.children.filter((c) => c.path !== '/cashIn');
    } else {
      depositMenu.children = depositMenu.children.filter((c) => c.path === '/cashIn');

      if (depositMenu.children.length === 1) {
        const [only] = depositMenu.children;
        depositMenu.path = only.path;
        depositMenu.type = 'link';
        depositMenu.Menusub = false;
        depositMenu.children = undefined;
      }
    }
  }

  private withdrawMenu(menu: Menu[]): void {
    const withdraw = menu.find((m) => m.title === 'common.withdraw');
    if (!withdraw || !withdraw.children) return;

    if (withdraw.locked) {
      withdraw.path = undefined;
      withdraw.children = undefined;
      withdraw.type = 'link';
      withdraw.Menusub = false;
      return;
    }

    const origin = this.authService.getOriginCurrency();

    if (origin !== 'BRL') {
      const [firstChild] = withdraw.children;
      withdraw.locked = true;
      withdraw.path = firstChild?.path || '';
      withdraw.type = 'link';
      withdraw.Menusub = false;
      withdraw.children = undefined;
    }
  }

  private setNoCrypto(menu: Menu[]): Menu[] {
    if (!this.authService.isNoCrypto()) {
      return menu;
    }

    const filtered = menu.filter(
      (m) =>
        m.title !== 'common.cryptoassets' &&
        m.title !== 'common.cryptoConversion'
    );

    const accountMenu = filtered.find((m) => m.title === 'common.account');

    if (accountMenu?.children) {
      accountMenu.children = accountMenu.children.filter(
        (child) =>
          child.title !== 'common.cryptoWallet' &&
          child.title !== 'common.webHook'
      );
    }

    return filtered;
  }

  private setCheckoutConfigVisibility(menu: Menu[]): Menu[] {
    const origin = (this.authService.getOriginCurrency() || 'BRL').toUpperCase();

    if (origin === 'BRL') {
      return menu.filter((m) => m.path !== '/checkout-config');
    }

    return menu;
  }

  private applyApiCredentialsVisibility(menu: Menu[]): void {
    if (this.apiCredentialsVisible) return;

    const accountMenu = menu.find((m) => m.title === 'common.account');
    if (!accountMenu?.children) return;

    accountMenu.children = accountMenu.children.filter(
      (child) => child.path !== '/api-credentials'
    );
  }

  private async loadMenuItems(): Promise<void> {
    let menu: Menu[];

    if (this.authService.isAdmin()) {
      menu = [...this.MENU_ADMIN];
    } else if (this.authService.isCrypto()) {
      menu = [...this.MENU_CRYPTO];
    } else {
      menu = this.buildCommonMenu();
      this.depositMenu(menu);
      this.withdrawMenu(menu);
      menu = this.setNoCrypto(menu);
      menu = this.setCheckoutConfigVisibility(menu);
      this.applyApiCredentialsVisibility(menu);
      menu = await this.lotteryAccessService.applyMenuRules(menu);
    }

    this.MENUITEMS = menu;
    this.items.next(menu);
  }

  private checkApiCredentialsVisibility(): void {
    this.http
      .get<any>(`${environment.apiUrl}/getaccountkeymulti`)
      .subscribe({
        next: (res) => {
          const list = res?.getAccountKeyOriginResponse ?? [];
          this.apiCredentialsVisible = Array.isArray(list) && list.length > 0;
          this.loadMenuItems();
        },
        error: () => {
          this.apiCredentialsVisible = false;
          this.loadMenuItems();
        },
      });
  }

  private findFirstAvailablePath(items: Menu[]): string | null {
    for (const item of items) {
      if (item.locked) continue;

      if (item.type === 'link' && item.path) {
        return item.path;
      }

      if (item.children?.length) {
        const childPath = this.findFirstAvailablePath(item.children);
        if (childPath) {
          return childPath;
        }
      }
    }

    return null;
  }

  public getFirstAvailableRoute(): string {
    const items = this.items.value?.length
      ? this.items.value
      : this.MENUITEMS;

    return this.findFirstAvailablePath(items) || '/account/view';
  }

  public async refreshMenuItems(): Promise<void> {
    await this.loadMenuItems();
  }
}